// MOSAICO de camas institucionales con radar EKH. Lectura por SITIO autorizado en lote; la cola
// de casos y las prioridades del centro NO se tocan: esto es contexto de signos, no alarmas.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Pause, Radio, WifiOff } from "lucide-react";
import api from "@/lib/api";
import CamaEkhCard from "@/components/ekh/CamaEkhCard";
import EkhDetalleCama from "@/components/ekh/EkhDetalleCama";
import useDocumentoVisible from "@/hooks/useDocumentoVisible";
import { conSenal } from "@/lib/ekh/vista";

const REFRESCO_MS = 5000;
// R02 · H3: un sitio SIN camas EKH sigue consultando, despacio, para detectar un radar asignado
// sin recargar (la sección no se muestra mientras está vacío y no hay nada que desplegar).
const VACIO_MS = 30000;
const CURVAS_VIVAS_MAX = 12;

export default function MosaicoEkh({ sitioId, casos = null, onAbrirCaso = null }) {
  const [data, setData] = useState(null);
  const [fallo, setFallo] = useState(false);
  // H5 · 403/404 = veredicto de alcance: se deja de consultar (sin bucle de reintentos).
  const [sinAcceso, setSinAcceso] = useState(false);
  // VIS · la sección entra COLAPSADA; la elección se conserva durante la visita a la pantalla
  // (los refrescos de datos no la reinician).
  const [abierto, setAbierto] = useState(false);
  const [selId, setSelId] = useState(null);
  // ID4 · los LECTORES EKH se pausan con la pestaña oculta o la sección plegada. La primera lectura
  // del mosaico se hace igual (decide si la sección existe y su conteo); al volver a la vista o al
  // desplegar, la lectura es inmediata. Eventos, alertas y atención no dependen de esto.
  const visible = useDocumentoVisible();
  const lectoresActivos = visible && abierto;
  // Cadencia del mosaico: 5 s desplegado; plegado, sólo hasta tener el mosaico del sitio VIGENTE y,
  // si el sitio no tiene camas EKH, cada 30 s. Con la pestaña oculta, nada.
  const delSitio = data?.sitio_id === sitioId;
  let intervalo = null;
  if (visible) {
    if (abierto || !delSitio) intervalo = REFRESCO_MS;
    else if (!(data?.camas || []).length) intervalo = VACIO_MS;
  }
  const ultimaLecturaRef = useRef({ en: 0, sitio: null });
  const intervaloPrevRef = useRef(null);

  const cargar = useCallback(async () => {
    if (!sitioId) return;
    ultimaLecturaRef.current = { en: Date.now(), sitio: sitioId };
    try {
      const { data: d } = await api.get(`/ekh/vista/sitios/${sitioId}/mosaico`);
      setData(d);
      setFallo(false);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403 || st === 404) setSinAcceso(true);
      setFallo(true);
    }
  }, [sitioId]);

  useEffect(() => {
    const previo = intervaloPrevRef.current;
    intervaloPrevRef.current = intervalo;
    if (sinAcceso || intervalo == null) return undefined;
    // Inmediata al reanudar (visible, desplegado) o al cambiar de sitio; al pasar de una cadencia a
    // otra (p. ej. de 5 s a 30 s por sitio vacío) se respeta el tiempo ya transcurrido.
    const transcurrido = Date.now() - ultimaLecturaRef.current.en;
    const inmediata = previo == null || ultimaLecturaRef.current.sitio !== sitioId || transcurrido >= intervalo;
    let t = null;
    let inicio = null;
    const arrancar = () => { cargar(); t = setInterval(cargar, intervalo); };
    if (inmediata) arrancar();
    else inicio = setTimeout(arrancar, intervalo - transcurrido);
    return () => { if (inicio) clearTimeout(inicio); if (t) clearInterval(t); };
  }, [cargar, sinAcceso, intervalo, sitioId]);
  useEffect(() => { setSinAcceso(false); setSelId(null); }, [sitioId]);

  // v2.20.1 · las camas CON señal van primero; las que no reportan conservan su sitio al final.
  const camas = useMemo(() => {
    const filas = data?.camas || [];
    return [...filas].sort((a, b) => Number(conSenal(b)) - Number(conSenal(a)));
  }, [data]);
  const seleccionada = useMemo(
    () => camas.find((c) => (c.cama_id || c.device_id) === selId) || null, [camas, selId]);

  // v2.20.3 · las mini curvas NO dependen de FC/FR ni de la presencia: las amplitudes del canal
  // rápido se consultan en toda cama EKH válida (con cama acreditada). Se conserva el TOPE de
  // suscripciones: un centro grande no dispara una consulta por cama y por segundo.
  const curvasVivas = useMemo(() => {
    const ids = new Set();
    for (const c of camas) {
      if (!c.cama_id || ids.size >= CURVAS_VIVAS_MAX) continue;
      ids.add(c.cama_id);
    }
    return ids;
  }, [camas]);

  if (!sitioId || sinAcceso || (!camas.length && !fallo)) return null;

  return (
    <section data-testid="ekh-mosaico" className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" data-testid="ekh-mosaico-toggle" onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="inline-flex min-h-9 items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wider text-[#012472]">
          {abierto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Residentes con monitoreo EKH
        </button>
        {/* R02 · H4: en pausa el conteo es el ÚLTIMO leído, no una lectura vigente: se dice en
            texto visible y con estilo neutro (también en CentroV3, que usa este componente). */}
        <span data-testid="ekh-mosaico-estado"
          data-pausado={lectoresActivos ? "no" : "si"}
          title={lectoresActivos ? undefined : "Lectura en pausa mientras la sección está plegada o la pestaña no está a la vista."}
          className={`gv2-chip inline-flex shrink-0 items-center ${fallo ? "gv2-chip-alert" : lectoresActivos ? "gv2-chip-live" : "gv2-chip-neutral"}`}>
          {fallo ? <WifiOff className="h-3.5 w-3.5" /> : lectoresActivos ? <Radio className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          {fallo ? "SIN ACTUALIZAR" : lectoresActivos
            ? `${data?.lectura?.con_datos ?? 0}/${data?.lectura?.equipos ?? 0} con lectura`
            : `En pausa · último conteo ${data?.lectura?.con_datos ?? 0}/${data?.lectura?.equipos ?? 0}`}
        </span>
      </div>

      {/* Colapsar sólo OCULTA: el contenido sigue montado y conserva su estado. ID4 · sus
          lectores EKH (tarjetas, mini curvas y ficha) se pausan mientras está plegado y se
          reanudan de inmediato al desplegar. Las alertas y la atención no viven aquí. */}
      <div className={abierto ? "" : "hidden"} aria-hidden={!abierto}>
        <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:overflow-hidden">
          {/* H6 · con la ficha abierta el mosaico cede ancho: menos columnas y más legibles, en
              vez de cuatro columnas estrechas con el nombre truncado. */}
          <div data-testid="ekh-mosaico-grid"
            data-ficha={seleccionada?.cama_id ? "abierta" : "cerrada"}
            className={`grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 ${
              seleccionada?.cama_id ? "xl:grid-cols-2" : "xl:grid-cols-3 2xl:grid-cols-4"}`}>
            {camas.map((c) => (
              <CamaEkhCard key={c.device_id} fila={c}
                curvaActiva={curvasVivas.has(c.cama_id)}
                lecturaActiva={lectoresActivos}
                seleccionada={(c.cama_id || c.device_id) === selId}
                onSeleccionar={(f) => setSelId(
                  (f.cama_id || f.device_id) === selId ? null : (f.cama_id || f.device_id))} />
            ))}
            {!camas.length && (
              <p data-testid="ekh-mosaico-vacio" className="col-span-full rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">
                Este centro no tiene radares EKH registrados o su lectura no se pudo consultar.
              </p>
            )}
          </div>
          {seleccionada?.cama_id && (
            <EkhDetalleCama key={seleccionada.cama_id} fila={seleccionada} onCerrar={() => setSelId(null)}
              activo={lectoresActivos}
              casos={casos} onAbrirCaso={onAbrirCaso} />
          )}
        </div>
      </div>
    </section>
  );
}
